<script setup lang="ts">
import { computed, ref } from 'vue'
import { jsonTokens, type JsonTokenKind } from './json-tokens'
import type { ServiceExampleData } from './service-example'

const props = defineProps<{
  example: ServiceExampleData
}>()

const open = ref(false)

const panes = computed(() => [
  { label: 'Input', blocks: props.example.input },
  { label: 'Output', blocks: props.example.output },
])

// The colours of the app's body viewer (CodeBodyViewer.vue:106-115), as Bootstrap text classes.
const TOKEN_CLASS: Record<JsonTokenKind, string> = {
  property: 'text-info-emphasis',
  string: 'text-danger-emphasis',
  literal: 'text-success-emphasis',
  punctuation: 'text-body-secondary',
  plain: '',
}
</script>

<template>
  <div class="mb-3">
    <button
      type="button"
      class="btn btn-link btn-sm text-secondary text-decoration-none px-0"
      :aria-expanded="open"
      @click="open = !open"
    >
      <i class="bi small me-1" :class="open ? 'bi-chevron-down' : 'bi-chevron-right'"></i>Example
    </button>

    <div v-if="open" class="row g-2">
      <div v-for="pane in panes" :key="pane.label" class="col-sm-6">
        <div class="border rounded bg-body-tertiary px-2 py-1 overflow-auto example-pane">
          <div class="small text-body-secondary">{{ pane.label }}</div>
          <template v-for="(block, blockIndex) in pane.blocks" :key="blockIndex">
            <div v-if="block.title" class="small text-body-secondary mt-1">{{ block.title }}</div>
            <!-- Each <pre> stays on one line: a line break inside it would be drawn. -->
            <pre v-for="(doc, docIndex) in block.docs ?? []" :key="docIndex" class="mb-0"><span v-for="(token, tokenIndex) in jsonTokens(doc)" :key="tokenIndex" :class="TOKEN_CLASS[token.kind]">{{ token.text }}</span></pre>
            <pre v-if="block.lines" class="mb-0">{{ block.lines.join('\n') }}</pre>
            <p v-if="block.note" class="small text-body-secondary mb-0">{{ block.note }}</p>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* A long example scrolls inside its pane, so the form under it stays in reach. */
.example-pane {
  max-height: 17rem;
}
</style>
